/* Luxury Wall Art - cookie consent banner + preference toggles.
 *
 * Everything routes through Shopify's Customer Privacy API, so consent is
 * recorded exactly as Shopify's own banner records it and downstream pixels
 * (GA4, Meta, Klaviyo) keep receiving the correct consent signals.
 *
 * The banner is only rendered when shouldShowBanner() is true, which Shopify
 * returns for visitors in regions whose law requires opt-in consent. Everyone
 * else never sees it and manages choices from /pages/your-privacy-choices,
 * linked in the footer.
 */
(function () {
  'use strict';

  var CATEGORIES = [
    {
      key: 'analytics',
      name: 'Analytics',
      desc: 'Which pages and prints get viewed, so we know what to make more of.'
    },
    {
      key: 'marketing',
      name: 'Marketing',
      desc: 'Lets us show our art to you on other sites and measure whether it worked.'
    },
    {
      key: 'preferences',
      name: 'Preferences',
      desc: 'Remembers things like your currency, wishlist and recently viewed art.'
    }
  ];

  var PRIVACY_URL = '/policies/privacy-policy';
  var CHOICES_URL = '/pages/your-privacy-choices';

  /* ------------------------------------------------------------ API glue --- */

  function api() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  function withApi(callback) {
    if (api()) {
      callback(api());
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error || !api()) {
          return;
        }
        callback(api());
      }
    );
  }

  function currentConsent(privacy) {
    var consent = {};

    try {
      consent = privacy.currentVisitorConsent() || {};
    } catch (e) {
      consent = {};
    }

    return {
      analytics: consent.analytics === 'yes',
      marketing: consent.marketing === 'yes',
      preferences: consent.preferences === 'yes'
    };
  }

  function saleOfDataRegion(privacy) {
    try {
      return typeof privacy.saleOfDataRegion === 'function' && privacy.saleOfDataRegion();
    } catch (e) {
      return false;
    }
  }

  function save(privacy, choices, done) {
    var payload = {
      analytics: !!choices.analytics,
      marketing: !!choices.marketing,
      preferences: !!choices.preferences
    };

    // sale_of_data only means something in US states that grant an opt out.
    // Sending it elsewhere is rejected, so it is set only where it applies.
    if (saleOfDataRegion(privacy)) {
      payload.sale_of_data = !!choices.marketing;
    }

    try {
      privacy.setTrackingConsent(payload, function (result) {
        if (typeof done === 'function') {
          done(!result || !result.error);
        }
      });
    } catch (e) {
      if (typeof done === 'function') {
        done(false);
      }
    }
  }

  /* ------------------------------------------------------- toggle markup --- */

  function buildToggles(idPrefix, choices) {
    var panel = document.createElement('div');
    panel.className = 'lwa-cc__panel';

    var essential = document.createElement('div');
    essential.className = 'lwa-cc__row';
    essential.innerHTML =
      '<div class="lwa-cc__row-body">' +
      '<span class="lwa-cc__row-name">Essential</span>' +
      '<span class="lwa-cc__row-desc">Needed for the cart, checkout and security. These cannot be switched off.</span>' +
      '</div>' +
      '<span class="lwa-cc__locked">Always on</span>';
    panel.appendChild(essential);

    CATEGORIES.forEach(function (category) {
      var id = idPrefix + '-' + category.key;
      var row = document.createElement('div');
      row.className = 'lwa-cc__row';
      row.innerHTML =
        '<div class="lwa-cc__row-body">' +
        '<label class="lwa-cc__row-name" for="' + id + '">' + category.name + '</label>' +
        '<span class="lwa-cc__row-desc">' + category.desc + '</span>' +
        '</div>' +
        '<span class="lwa-cc__switch">' +
        '<input type="checkbox" id="' + id + '" data-lwa-cc-cat="' + category.key + '"' +
        (choices[category.key] ? ' checked' : '') + '>' +
        '<span class="lwa-cc__track" aria-hidden="true"></span>' +
        '</span>';
      panel.appendChild(row);
    });

    return panel;
  }

  function readToggles(panel) {
    var choices = {};

    CATEGORIES.forEach(function (category) {
      var input = panel.querySelector('[data-lwa-cc-cat="' + category.key + '"]');
      choices[category.key] = !!(input && input.checked);
    });

    return choices;
  }

  /* -------------------------------------------------------------- banner --- */

  function buildBanner(privacy) {
    var wrap = document.createElement('section');
    wrap.className = 'lwa-cc';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-live', 'polite');
    wrap.setAttribute('aria-label', 'Cookie choices');

    wrap.innerHTML =
      '<div class="lwa-cc__head">' +
      '<svg class="lwa-cc__mark" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M12 3a9 9 0 1 0 9 9 4.2 4.2 0 0 1-5-5 4.2 4.2 0 0 1-4-4Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
      '<circle cx="9.4" cy="13.6" r="1.05" fill="currentColor"/>' +
      '<circle cx="13.7" cy="15.9" r="1.05" fill="currentColor"/>' +
      '<circle cx="8.8" cy="9.2" r="1.05" fill="currentColor"/>' +
      '</svg>' +
      '<h2 class="lwa-cc__title">Cookies</h2>' +
      '</div>' +
      '<p class="lwa-cc__text">We use cookies to run the store and to learn which art people love. ' +
      'You can change your mind any time on our <a href="' + CHOICES_URL + '">privacy choices</a> page.</p>' +
      '<div class="lwa-cc__actions">' +
      '<button type="button" class="lwa-cc__btn lwa-cc__btn--primary" data-lwa-cc="accept">Accept all</button>' +
      '<button type="button" class="lwa-cc__btn lwa-cc__btn--ghost" data-lwa-cc="reject">Essential only</button>' +
      '<button type="button" class="lwa-cc__link" data-lwa-cc="customise" aria-expanded="false">Choose</button>' +
      '</div>';

    var actions = wrap.querySelector('.lwa-cc__actions');
    var panel = buildToggles('lwa-cc-banner', currentConsent(privacy));
    panel.hidden = true;
    wrap.insertBefore(panel, actions);

    function close() {
      wrap.hidden = true;
      if (wrap.parentNode) {
        wrap.parentNode.removeChild(wrap);
      }
    }

    wrap.addEventListener('click', function (event) {
      var action = event.target.getAttribute && event.target.getAttribute('data-lwa-cc');

      if (action === 'accept') {
        save(privacy, { analytics: true, marketing: true, preferences: true }, close);
        return;
      }

      if (action === 'reject') {
        save(privacy, { analytics: false, marketing: false, preferences: false }, close);
        return;
      }

      if (action === 'save') {
        save(privacy, readToggles(panel), close);
        return;
      }

      if (action === 'customise') {
        var button = event.target;
        panel.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        button.remove();

        var accept = wrap.querySelector('[data-lwa-cc="accept"]');
        accept.textContent = 'Save choices';
        accept.setAttribute('data-lwa-cc', 'save');

        var reject = wrap.querySelector('[data-lwa-cc="reject"]');
        reject.textContent = 'Reject all';
      }
    });

    return wrap;
  }

  function mountBanner(privacy) {
    var shouldShow = true;

    try {
      if (typeof privacy.shouldShowBanner === 'function') {
        shouldShow = privacy.shouldShowBanner();
      }
    } catch (e) {
      shouldShow = false;
    }

    if (!shouldShow || document.querySelector('.lwa-cc')) {
      return;
    }

    document.body.appendChild(buildBanner(privacy));
  }

  /* ---------------------------------------------------- page preferences --- */

  function mountPagePanel(privacy) {
    var host = document.getElementById('lwa-cookie-preferences');

    if (!host || host.getAttribute('data-lwa-cc-ready') === 'true') {
      return;
    }

    host.setAttribute('data-lwa-cc-ready', 'true');
    host.className = 'lwa-ccp';

    var heading = document.createElement('h2');
    heading.className = 'lwa-ccp__title';
    heading.textContent = 'Manage cookie preferences';
    host.appendChild(heading);

    var intro = document.createElement('p');
    intro.className = 'lwa-ccp__intro';
    intro.textContent = 'Switch each type on or off, then save. Your choice applies to this browser straight away.';
    host.appendChild(intro);

    var panel = buildToggles('lwa-cc-page', currentConsent(privacy));
    host.appendChild(panel);

    var foot = document.createElement('div');
    foot.className = 'lwa-ccp__foot';

    var saveButton = document.createElement('button');
    saveButton.type = 'button';
    saveButton.className = 'lwa-cc__btn lwa-cc__btn--primary';
    saveButton.textContent = 'Save preferences';
    foot.appendChild(saveButton);

    var status = document.createElement('span');
    status.className = 'lwa-ccp__status';
    status.setAttribute('role', 'status');
    foot.appendChild(status);

    host.appendChild(foot);

    var note = document.createElement('p');
    note.className = 'lwa-ccp__note';
    note.innerHTML =
      'Essential cookies keep the cart and checkout working, so they stay on. ' +
      'Full detail on what we collect is in our <a href="' + PRIVACY_URL + '">Privacy Policy</a>.';
    host.appendChild(note);

    saveButton.addEventListener('click', function () {
      save(privacy, readToggles(panel), function (ok) {
        status.textContent = ok
          ? 'Saved. Your preferences are up to date.'
          : 'We could not save that. Please try again.';
        status.style.color = ok ? '' : '#c0392b';
        status.classList.add('is-visible');

        window.setTimeout(function () {
          status.classList.remove('is-visible');
        }, 4000);
      });
    });
  }

  /* ----------------------------------------------------------------- go --- */

  function start() {
    withApi(function (privacy) {
      mountBanner(privacy);
      mountPagePanel(privacy);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
